<template>
  <div>
    <!-- Content Container -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div 
          v-for="client in (clients?.data || [])" 
          :key="client.id" 
          class="p-4 hover:bg-gray-50 transition-colors" 
          :class="{'bg-red-50/60': client.balance > 0}"
        >
          <Link :href="`/clients/${client.id}`" class="space-y-3 block">
            <div class="flex justify-between items-start">
              <div>
                <h3 class="font-bold text-gray-900">{{ client.companyName || client.name }}</h3>
                <p v-if="client.companyName" class="text-sm text-gray-500">{{ client.name }}</p>
              </div>
              <span 
                v-if="client.balance > 0" 
                class="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-bold uppercase tracking-wider"
              >
                Deuda
              </span>
            </div>
            
            <div class="text-sm text-gray-600 space-y-1">
              <div class="flex items-center gap-2">
                <i class="fa fa-id-card text-gray-400 w-4" />
                <span>{{ client.identification || '-' }}</span>
              </div>
              <div v-if="client.email" class="flex items-center gap-2">
                <i class="fa fa-envelope text-gray-400 w-4" />
                <span class="truncate">{{ client.email }}</span>
              </div>
              <div v-if="client.phone" class="flex items-center gap-2">
                <i class="fa fa-phone text-gray-400 w-4" />
                <span>{{ client.phone }}</span>
              </div>
            </div>

            <div class="pt-2 flex justify-between items-center border-t border-gray-100 mt-2">
              <span class="font-bold text-base" :class="client.balance > 0 ? 'text-red-600' : 'text-gray-900'">
                {{ formatCurrency(client.balance) }}
              </span>
              <div class="flex items-center gap-2 text-gray-400">
                <button 
                  type="button"
                  class="p-1 hover:text-red-600 transition-colors" 
                  title="Eliminar cliente"
                  @click.prevent.stop="$emit('delete', client)"
                >
                  <i class="fa fa-trash" />
                </button>
                <i class="fa fa-chevron-right text-gray-300" />
              </div>
            </div>
          </Link>
        </div>

        <!-- Mobile Empty State -->
        <div v-if="!clients || (clients.data && clients.data.length === 0)" class="p-8 text-center text-gray-400">
          <i class="fa fa-users text-4xl mb-3 text-gray-300" />
          <h3 class="text-base font-bold text-gray-800">No hay clientes disponibles</h3>
          <p class="text-xs text-gray-500 mt-1">Crea un cliente o importa una lista para comenzar.</p>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/70 text-left border-b border-gray-100">
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Empresa</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Nombre</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Alias / Cédula</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Correo</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Dirección</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Adeudado</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Teléfono</th>
              <th class="py-3.5 px-6" />
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="client in (clients?.data || [])" 
              :key="client.id" 
              class="group hover:bg-gray-50 transition-colors"
              :class="{'bg-red-50/40 hover:bg-red-50/80': client.balance > 0}"
            >
              <td class="px-6 py-4">
                <Link :href="`/clients/${client.id}`" class="block">
                  <span class="font-medium text-gray-900">{{ client.companyName || '-' }}</span>
                </Link>
              </td>
              <td class="px-6 py-4">
                <Link :href="`/clients/${client.id}`" class="flex items-center gap-2">
                  <span class="text-gray-700 font-medium">{{ client.name }}</span>
                  <span v-if="client.deleted_at" class="text-red-500 text-xs" title="Eliminado">
                    <i class="fa fa-trash" />
                  </span>
                </Link>
              </td>
              <td class="px-6 py-4 text-gray-600 text-sm">{{ client.identification || '-' }}</td>
              <td class="px-6 py-4 text-gray-600 text-sm">{{ client.email || '-' }}</td>
              <td class="px-6 py-4 text-gray-600 text-sm max-w-xs truncate" :title="client.address">
                {{ client.address || '-' }}
              </td>
              <td class="px-6 py-4 text-right font-bold text-sm" :class="client.balance > 0 ? 'text-red-600' : 'text-gray-900'">
                {{ formatCurrency(client.balance) }}
              </td>
              <td class="px-6 py-4 text-gray-600 text-sm">{{ client.phone || '-' }}</td>
              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-3">
                  <button 
                    type="button"
                    class="text-gray-400 hover:text-red-600 transition-colors p-1" 
                    title="Eliminar cliente"
                    @click.stop="$emit('delete', client)"
                  >
                    <i class="fa fa-trash" />
                  </button>
                  <Link :href="`/clients/${client.id}`" class="text-gray-400 hover:text-orange-500 transition-colors p-1" title="Ver detalle">
                    <i class="fa fa-chevron-right text-xs" />
                  </Link>
                </div>
              </td>
            </tr>

            <!-- Empty State Desktop -->
            <tr v-if="!clients || (clients.data && clients.data.length === 0)">
              <td colspan="8" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-users text-2xl opacity-50" />
                  </div>
                  <h3 class="text-base font-bold text-gray-900 mb-1">No hay clientes disponibles</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Empieza por crear un nuevo cliente o importa un listado masivo en Excel.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="clients?.links" class="mt-6 flex justify-center">
      <pagination :links="clients.links" />
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Pagination from '@/Shared/Pagination'

export default {
  name: 'ClientTable',

  components: {
    Link,
    Pagination,
  },

  props: {
    clients: {
      type: Object,
      required: true,
    },
  },

  emits: ['delete'],

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0)
    },
  },
}
</script>
